import { useCallback, useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardCheck, Lock, Check, AlertTriangle, Camera, Loader2, CalendarClock, ChevronRight, Sparkles,
} from "lucide-react";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return d; } };
const fmtPeriod = (p) => { try { const [y, m] = p.split("-"); return new Date(y, m - 1, 1).toLocaleDateString(undefined, { year: "numeric", month: "long" }); } catch { return p; } };
const nextPeriodLabel = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return d.toLocaleDateString(undefined, { year: "numeric", month: "long" }); };

export function MonthlyCheckup({ propertyId, readOnly }) {
  const { isPremium, openUpgrade } = useTier();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flowOpen, setFlowOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    if (!isPremium) { setLoading(false); return; }
    try { const { data } = await api.get(`/properties/${propertyId}/checkups`); setData(data); }
    catch { /* interceptor handles 402 */ }
    finally { setLoading(false); }
  }, [propertyId, isPremium]);

  useEffect(() => { load(); }, [load]);

  if (!isPremium) {
    return (
      <div data-testid="checkup-locked" className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#F1F5F9] text-[#94A3B8]"><Lock size={16} /></span>
          <h2 className="font-heading text-lg font-semibold text-[#64748B]">Monthly Rental Checkup</h2>
        </div>
        <p className="mt-2 text-sm text-[#94A3B8]">Create a timestamped monthly record of your rental's condition. TenantSeal Plus feature.</p>
        <Button data-testid="checkup-upgrade-btn" onClick={() => openUpgrade("monthly_checkup")}
          className="mt-4 h-11 rounded-xl bg-navy hover:bg-navy-hover text-white font-semibold">
          <Sparkles size={16} className="mr-2" /> Upgrade to TenantSeal Plus — $6/mo
        </Button>
      </div>
    );
  }

  const current = data?.current_period;
  const done = (data?.checkups || []).find((c) => c.period === current && c.completed_at);
  const history = (data?.checkups || []).filter((c) => c.period !== current);

  return (
    <div data-testid="monthly-checkup" className="rounded-2xl border border-brand/25 bg-brand/[0.04] p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <ClipboardCheck size={20} className="text-brand" />
        <h2 className="font-heading text-lg font-bold text-navy">Monthly Rental Checkup</h2>
      </div>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[#64748B]"><Loader2 size={15} className="animate-spin" /> Loading…</div>
      ) : done ? (
        <div className="mt-4 rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] px-4 py-4" data-testid="checkup-completed">
          <div className="flex items-center gap-2 text-[#166534] font-semibold text-sm">
            <Check size={16} /> {fmtPeriod(current)} checkup complete
          </div>
          <p className="mt-1 text-xs text-[#15803D]">Completed {fmtDate(done.completed_at)}. Next checkup: {nextPeriodLabel()}.</p>
          <button data-testid="checkup-view-current" onClick={() => setViewing(done)} className="mt-2 text-xs text-brand hover:underline">View details</button>
        </div>
      ) : (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-[#475569]">Not started for {fmtPeriod(current)} — a couple of minutes builds your record.</p>
          {!readOnly && (
            <Button data-testid="checkup-start-btn" onClick={() => setFlowOpen(true)}
              className="rounded-xl h-11 bg-navy hover:bg-navy-hover text-white shrink-0">Start checkup</Button>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">History</p>
          <div className="mt-2 space-y-2">
            {history.map((c) => {
              const issues = (c.items || []).filter((i) => i.status === "issue").length;
              return (
                <button key={c.checkup_id} data-testid={`checkup-history-${c.period}`} onClick={() => setViewing(c)}
                  className="w-full flex items-center justify-between gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-left hover:border-brand transition-colors">
                  <div>
                    <p className="text-sm font-semibold text-navy">{fmtPeriod(c.period)}</p>
                    <p className="text-xs text-[#94A3B8]">{issues > 0 ? `${issues} issue${issues > 1 ? "s" : ""} flagged` : "All clear"} · {fmtDate(c.completed_at)}</p>
                  </div>
                  <ChevronRight size={16} className="text-[#CBD5E1]" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {flowOpen && (
        <CheckupFlow propertyId={propertyId} areas={data?.areas || []}
          onClose={() => setFlowOpen(false)}
          onDone={() => { setFlowOpen(false); load(); }} />
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg" data-testid="checkup-view-dialog">
          <DialogHeader><DialogTitle>{viewing ? fmtPeriod(viewing.period) : ""} checkup</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(viewing?.items || []).map((it, i) => (
              <div key={i} className="rounded-xl border border-[#E2E8F0] p-3">
                <div className="flex items-center gap-2">
                  {it.status === "issue" ? <AlertTriangle size={15} className="text-amber-ink" /> : <Check size={15} className="text-[#16A34A]" />}
                  <span className="text-sm font-semibold text-navy">{it.area}</span>
                  <span className={`ml-auto text-xs font-medium ${it.status === "issue" ? "text-amber-ink" : "text-[#16A34A]"}`}>{it.status === "issue" ? "Issue" : "OK"}</span>
                </div>
                {it.note && <p className="mt-1.5 text-xs text-[#475569]">{it.note}</p>}
                {(it.photos || []).length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {it.photos.map((p) => (<img key={p.photo_id} src={fileUrl(p.storage_path)} alt="" className="h-16 w-16 rounded-lg object-cover border border-[#E2E8F0]" />))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CheckupFlow({ propertyId, areas, onClose, onDone }) {
  const [items, setItems] = useState(() => areas.map((a) => ({ area: a, status: "ok", note: "", photo_ids: [] })));
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (i, patch) => setItems((s) => s.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const addPhoto = async (i, file) => {
    if (!file) return;
    setUploading(i);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/properties/${propertyId}/checkups/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(i, { photo_ids: [...items[i].photo_ids, data.photo_id] });
    } catch { toast.error("Photo upload failed"); }
    finally { setUploading(null); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/properties/${propertyId}/checkups`, { items });
      toast.success("Checkup saved");
      onDone();
    } catch { toast.error("Could not save checkup"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="checkup-flow-dialog">
        <DialogHeader><DialogTitle>Monthly checkup</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[62vh] overflow-y-auto pr-1">
          {items.map((it, i) => (
            <div key={it.area} className="rounded-xl border border-[#E2E8F0] p-3" data-testid={`checkup-item-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-navy">{it.area}</span>
                <div className="flex gap-1.5">
                  <button data-testid={`checkup-ok-${i}`} onClick={() => update(i, { status: "ok" })}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${it.status === "ok" ? "bg-[#DCFCE7] border-[#86EFAC] text-[#166534]" : "border-[#E2E8F0] text-[#94A3B8]"}`}>OK</button>
                  <button data-testid={`checkup-issue-${i}`} onClick={() => update(i, { status: "issue" })}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${it.status === "issue" ? "bg-amber-soft border-amber-border text-amber-ink" : "border-[#E2E8F0] text-[#94A3B8]"}`}>Issue</button>
                </div>
              </div>
              {it.status === "issue" && (
                <Textarea data-testid={`checkup-note-${i}`} value={it.note} onChange={(e) => update(i, { note: e.target.value })}
                  placeholder="What did you notice?" rows={2} className="mt-2 rounded-xl text-sm" />
              )}
              <div className="mt-2 flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 text-xs text-brand cursor-pointer hover:underline">
                  {uploading === i ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />} Add photo
                  <input type="file" accept="image/*" className="hidden" data-testid={`checkup-photo-${i}`}
                    onChange={(e) => addPhoto(i, e.target.files?.[0])} />
                </label>
                {it.photo_ids.length > 0 && <span className="text-xs text-[#94A3B8]">{it.photo_ids.length} photo(s)</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-[#94A3B8] mt-1"><CalendarClock size={13} /> Saved as this month's snapshot.</div>
        <Button data-testid="checkup-submit-btn" onClick={submit} disabled={saving}
          className="mt-1 h-11 rounded-xl bg-navy hover:bg-navy-hover text-white font-semibold">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Complete checkup
        </Button>
      </DialogContent>
    </Dialog>
  );
}
